import { REACT_HOT_TOAST_ERROR, REACT_HOT_TOAST_SUCCESS } from "../../_CORE/HELPERS/reactHotToast.helper";

const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n += 1) {
        let c = n;
        for (let k = 0; k < 8; k += 1) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
        table[n] = c >>> 0;
    }
    return table;
})();

const onCrc32 = (bytes) => {
    let crc = 0xFFFFFFFF;
    for (let index = 0; index < bytes.length; index += 1) crc = CRC_TABLE[(crc ^ bytes[index]) & 0xFF] ^ (crc >>> 8);
    return (crc ^ 0xFFFFFFFF) >>> 0;
};

const onU16 = (value) => {
    const bytes = new Uint8Array(2);
    new DataView(bytes.buffer).setUint16(0, value, true);
    return bytes;
};

const onU32 = (value) => {
    const bytes = new Uint8Array(4);
    new DataView(bytes.buffer).setUint32(0, value >>> 0, true);
    return bytes;
};

const onJoinBytes = (parts) => {
    const size = parts.reduce((total, part) => total + part.length, 0);
    const output = new Uint8Array(size);
    let offset = 0;
    parts.forEach((part) => {
        output.set(part, offset);
        offset += part.length;
    });
    return output;
};

const onDosDateTime = (date = new Date()) => {
    const year = Math.max(1980, date.getFullYear());
    const dosTime = ((date.getHours() & 0x1F) << 11)
        | ((date.getMinutes() & 0x3F) << 5)
        | (Math.floor(date.getSeconds() / 2) & 0x1F);
    const dosDate = (((year - 1980) & 0x7F) << 9)
        | (((date.getMonth() + 1) & 0x0F) << 5)
        | (date.getDate() & 0x1F);
    return { dosDate, dosTime };
};

const onCreateStoredZip = (entries) => {
    const encoder = new TextEncoder();
    const localParts = [];
    const centralParts = [];
    const { dosDate, dosTime } = onDosDateTime();
    let localOffset = 0;

    entries.forEach((entry) => {
        const nameBytes = encoder.encode(String(entry.path || "archivo").replace(/\\/g, "/"));
        const dataBytes = entry.bytes instanceof Uint8Array ? entry.bytes : new Uint8Array(entry.bytes || []);
        const crc = onCrc32(dataBytes);

        const localHeader = onJoinBytes([
            onU32(0x04034B50), onU16(20), onU16(0x0800), onU16(0), onU16(dosTime), onU16(dosDate),
            onU32(crc), onU32(dataBytes.length), onU32(dataBytes.length), onU16(nameBytes.length), onU16(0), nameBytes,
        ]);
        localParts.push(localHeader, dataBytes);

        const centralHeader = onJoinBytes([
            onU32(0x02014B50), onU16(20), onU16(20), onU16(0x0800), onU16(0), onU16(dosTime), onU16(dosDate),
            onU32(crc), onU32(dataBytes.length), onU32(dataBytes.length), onU16(nameBytes.length), onU16(0), onU16(0),
            onU16(0), onU16(0), onU32(0), onU32(localOffset), nameBytes,
        ]);
        centralParts.push(centralHeader);
        localOffset += localHeader.length + dataBytes.length;
    });

    const central = onJoinBytes(centralParts);
    const end = onJoinBytes([
        onU32(0x06054B50), onU16(0), onU16(0), onU16(entries.length), onU16(entries.length),
        onU32(central.length), onU32(localOffset), onU16(0),
    ]);

    return onJoinBytes([...localParts, central, end]);
};

export const safeArchiveName = (value = "archivos") => String(value || "archivos")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[<>:"/\\|?*\u0000-\u001F]+/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[. ]+$/g, "") || "archivos";

const onSplitName = (name) => {
    const value = safeArchiveName(name || "archivo");
    const index = value.lastIndexOf(".");
    if (index <= 0 || index === value.length - 1) return { base: value, ext: "" };
    return { base: value.slice(0, index), ext: value.slice(index) };
};

const onUniqueName = (name, used) => {
    const { base, ext } = onSplitName(name);
    const key = `${base}${ext}`.toLowerCase();
    const current = (used.get(key) || 0) + 1;
    used.set(key, current);
    return current === 1 ? `${base}${ext}` : `${base} (${current})${ext}`;
};

const safeArchivePath = (value = "archivo") => String(value || "archivo")
    .replace(/\\/g, "/")
    .split("/")
    .filter((segment) => segment && segment !== "." && segment !== "..")
    .map((segment) => safeArchiveName(segment))
    .filter(Boolean)
    .join("/") || "archivo";

const onUniquePath = (path, used) => {
    const normalized = safeArchivePath(path);
    const slash = normalized.lastIndexOf("/");
    const directory = slash >= 0 ? normalized.slice(0, slash + 1) : "";
    const name = slash >= 0 ? normalized.slice(slash + 1) : normalized;
    const { base, ext } = onSplitName(name);
    const key = normalized.toLowerCase();
    const current = (used.get(key) || 0) + 1;
    used.set(key, current);
    return current === 1 ? normalized : `${directory}${base} (${current})${ext}`;
};

export const downloadBlobFile = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = fileName;
    window.document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

export const downloadFilesAsZip = async ({
    files = [],
    loadBlob,
    fileName = "archivos.zip",
    successMessage = null,
    errorMessage = "No se pudieron descargar todos los archivos.",
    pathForFile = null,
}) => {
    const list = Array.isArray(files) ? files.filter(Boolean) : [];
    if (!list.length || typeof loadBlob !== "function") return false;

    try {
        const used = new Map();
        const entries = [];

        for (let index = 0; index < list.length; index += 1) {
            const file = list[index];
            const blob = await loadBlob(file);
            if (!(blob instanceof Blob)) throw new Error("El servidor no devolvió un archivo válido.");
            const bytes = new Uint8Array(await blob.arrayBuffer());
            const sourceName = file.originalName || file.fileName || file.name || file.title || `archivo-${index + 1}`;
            const requestedPath = typeof pathForFile === "function" ? pathForFile(file, index) : null;
            entries.push({
                path: requestedPath ? onUniquePath(requestedPath, used) : onUniqueName(sourceName, used),
                bytes,
            });
        }

        const zipBytes = onCreateStoredZip(entries);
        const archiveName = safeArchiveName(String(fileName || "archivos.zip").replace(/\.zip$/i, ""));
        downloadBlobFile(new Blob([zipBytes], { type: "application/zip" }), `${archiveName}.zip`);
        REACT_HOT_TOAST_SUCCESS(successMessage || `${list.length} archivo${list.length === 1 ? "" : "s"} descargado${list.length === 1 ? "" : "s"} en ZIP`);
        return true;
    } catch (error) {
        REACT_HOT_TOAST_ERROR(error?.message ? `${errorMessage} ${error.message}` : errorMessage, { duration: 7000 });
        return false;
    }
};
